// components/Billing/ProcessSubCharge.jsx
// Import Libraries
import React, { useState, useEffect, useContext, useMemo } from 'react';
import Modal from 'react-modal';

// Import Components
import CheckoutForm from './CheckoutForm';
import StripeWrapper from './StripeWrapper';
import PlanList from '../Subscribe/planList';
import PromotionCode from './promotionCode';

// Import Services
import subService from '../../../services/subscriptionService';
import paymentService from '../../../services/paymentService';

// Context
import { UserContext } from '../../../context/User';
import SessionContext from '../../../context/Session';

// Setup Modal to Root
Modal.setAppElement('#root');

const ProcessSubCharge = ({ scriptId, nextStep, userDetails, orderInfo, setOrderInfo }) => {
  const { user } = useContext(UserContext);
  const { isMobile } = useContext(SessionContext);

  const useUser = userDetails || user;

  const [isLoading, setIsLoading] = useState(true);
  const [hasCards, setHasCards] = useState(false);
  const [newCard, setNewCard] = useState(false);

  // Keep local copies if child components want to use/preview them
  const [cards, setCards] = useState([]);
  const [defaultPmId, setDefaultPmId] = useState(null);

  const continueWithTrial = async () => {
    const submitted = await subService.createSubscription(useUser.id, 'trial', orderInfo, scriptId);
    if (submitted) nextStep(4);
  };

  const updatePromoCode = async (code) => {
    const c = code || {};
    let discount = 0;

    if (c.type === 'percent') discount = orderInfo.amountDue * (c.discount / 100);
    else if (c.type === 'fixed') discount = c.discount;

    setOrderInfo({
      ...orderInfo,
      couponCode: c,
      discount,
      amountDue: orderInfo.amountDue - discount,
    });
  };

  // Load saved cards (server source of truth), and validate plan selection
  useEffect(() => {
    let mounted = true;

    async function init() {
      try {
        setIsLoading(true);

        // Must have a selected plan; otherwise, send them to pick
        if (!orderInfo?.selectedPlan?.details) {
          nextStep(1);
          return;
        }

        // Fetch cards just like PaymentMethods
        const resp = await paymentService.getAvailableCards(useUser?.id);
        const paymentMethods = resp?.paymentMethods ?? (Array.isArray(resp) ? resp : []);
        const defaultPaymentMethodId = resp?.defaultPaymentMethodId ?? null;

        if (!mounted) return;

        setCards(paymentMethods);
        setDefaultPmId(defaultPaymentMethodId);

        const anyCards = paymentMethods.length > 0;
        setHasCards(anyCards);
        setNewCard(!anyCards); // if no cards, show new card entry
      } catch (_e) {
        // On error, fall back to new-card flow
        if (mounted) {
          setCards([]);
          setHasCards(false);
          setNewCard(true);
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    }

    init();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useUser?.id]);

  const dueOnText = useMemo(() => {
    const d = orderInfo?.dueDate ? new Date(orderInfo.dueDate) : new Date();
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [orderInfo?.dueDate]);

  if (!orderInfo?.selectedPlan) return null;

  return (
    <>
      <div className="modalHeader bold fullWidthContainer">
        <span className="TwoPortionFlex">Process Payment</span>
        <span className="OnePortionFlex" />

        {useUser.id !== user.id &&
          orderInfo.selectedPlan.details &&
          orderInfo.selectedPlan.details['include_trial'] && (
            <button className="TwoPortionFlex buttonClone MinSmall" onClick={continueWithTrial}>
              Skip Payment until after Trial
            </button>
          )}
      </div>

      <div className="modalContent" style={{ margin: isMobile ? '-10px' : '0px' }}>
        <div className="fullWidthContainer Padded" style={{ paddingBottom: '30px' }}>
          {/* Left column: plan + promo + totals */}
          <span
            className="OnePortionFlex Padded MinSmall"
            style={{ borderRight: isMobile ? '' : '1px solid #cccccc', alignSelf: 'baseline' }}
          >
            <div className="Padded" style={{ paddingBottom: '30px' }}>
              <div className="bold">
                Selected Plan:
                <br />
                <br />
              </div>

              <PlanList orderInfo={orderInfo} setOrderInfo={setOrderInfo} nextStep={nextStep} />

              <div className="fullWidthContainer">
                <div className="Padded fullWidthContainer">
                  <PromotionCode updatePromoCode={updatePromoCode} amount={orderInfo.amountDue} />
                </div>

                <div className="Padded fullWidthContainer">
                  <span className="bold OnePortionFlex">Renews</span>
                  <span className="OnePortionFlex" style={{ textAlign: 'right' }}>
                    {orderInfo.selectedPlan.label}
                  </span>
                </div>

                {orderInfo.discount ? (
                  <div className="Padded fullWidthContainer">
                    <span className="bold OnePortionFlex">Subtotal:</span>
                    <span className="OnePortionFlex" style={{ textAlign: 'right' }}>
                      {orderInfo.selectedPlan.details.price.toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                ) : null}

                {orderInfo.discount ? (
                  <div className="Padded fullWidthContainer">
                    <span className="bold OnePortionFlex">Discount:</span>
                    <span className="OnePortionFlex" style={{ textAlign: 'right' }}>
                      {orderInfo.discount.toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                ) : null}

                <div className="Padded fullWidthContainer">
                  <span className="bold OnePortionFlex">Total</span>
                  <span className="OnePortionFlex" style={{ textAlign: 'right' }}>
                    {orderInfo.amountDue.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </div>

                <div className="fullWidthContainer">&nbsp;</div>

                {orderInfo.includeTrial ? (
                  <div className="Padded fullWidthContainer">
                    <span className="bold OnePortionFlex">Due On</span>
                    <span className="OnePortionFlex" style={{ textAlign: 'right' }}>
                      {dueOnText}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </span>

          {/* Right column: payment UI */}
          {!isLoading && (
            <div
              className="DeepPadded OnePortionFlex"
              style={{ minWidth: '250px', paddingBottom: '30px', paddingTop: '0px' }}
            >
              <div
                className="fullWidthContainer"
                style={{
                  color: '#555',
                  padding: '0px 0px 25px 0px',
                }}
              >
                All payments are handled by{' '}
                <a
                  href="https://stripe.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#7A64EB', textDecoration: 'underline', display: 'contents' }}
                >
                  Stripe,
                </a>{' '}
                a third-party payment processing company certified to the highest industry security
                standards.
              </div>

              {hasCards && !newCard ? (
                <CheckoutForm
                  setNewCard={setNewCard}
                  scriptId={scriptId}
                  nextStep={nextStep}
                  orderInfo={orderInfo}
                  cards={cards}
                  defaultPaymentMethodId={defaultPmId}
                  userId={useUser?.id}
                />
              ) : (
                <StripeWrapper
                  hasCards={hasCards}
                  setNewCard={setNewCard}
                  scriptId={scriptId}
                  nextStep={nextStep}
                  orderInfo={orderInfo}
                  userId={useUser?.id}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProcessSubCharge;
